import store from "@/store";
import { EWebMethod, IRequest } from "@/struct/IRequest/IRequest";
import UtilStorage from "@/util/Common/UtilStorage";
import { App } from "@capacitor/app";
import { Capacitor, CapacitorHttp, HttpResponse } from "@capacitor/core";
import FingerprintJS from "@fingerprintjs/fingerprintjs";
import * as Sentry from "@sentry/vue";

class $api {
  private static fingerprint: string | null = null;

  private static getBaseUrl(): string {
    if (process.env.VUE_APP_BASE_URL) {
      return process.env.VUE_APP_BASE_URL.trim().replace(/\/+$/, "");
    }

    return window.location.origin;
  }

  private static async getFingerprint(): Promise<string> {
    if (!this.fingerprint) {
      const fp = await FingerprintJS.load();
      const result = await fp.get();
      this.fingerprint = result.visitorId;
    }
    return this.fingerprint;
  }

  private static readonly SKIP_RECOVERY_URLS = ["auth/is-telegram"];

  private static shouldSkipRecovery(url: string): boolean {
    return this.SKIP_RECOVERY_URLS.some((skipUrl) => url.includes(skipUrl));
  }

  private static normalizeUrl(url: string): string {
    url = url.replace(/\s+/g, "").replace(/\u00A0/g, "");

    if (url.startsWith("http://") || url.startsWith("https://")) {
      return url;
    }

    if (Capacitor.getPlatform() !== "web" && url.startsWith("capacitor://")) {
      return url;
    }
    let baseUrl = this.getBaseUrl();
    baseUrl = baseUrl.trim().replace(/\/+$/, "");
    const cleanUrl = url.trim().replace(/^\/+/, "");
    const normalizedUrl = `${baseUrl}/${cleanUrl}`;

    return normalizedUrl;
  }

  private static async makeNetworkRequest(
    options: any,
    method: EWebMethod,
    url: string,
  ): Promise<HttpResponse> {
    try {
      return await CapacitorHttp.request({
        ...options,
        method: method,
        url: url,
        webFetchExtra: {
          credentials: "include",
        },
      });
    } catch (capacitorError) {
      console.warn(capacitorError);

      try {
        const fetchResponse = await fetch(url, {
          method: method,
          headers: options.headers,
          body: options.data,
          credentials: "include",
        });

        let data;
        try {
          data = await fetchResponse.json();
        } catch (e) {
          data = { detail: "incorrect_response", status: fetchResponse.status };
        }

        return {
          url: url,
          status: fetchResponse.status,
          data: data,
          headers: Object.fromEntries(fetchResponse.headers.entries()),
        };
      } catch (fetchError) {
        console.error(fetchError);
        throw capacitorError;
      }
    }
  }

  public static async call(
    method: EWebMethod,
    url: string,
    data?: any,
    retryOnUnauthorized = true,
  ): Promise<any | HttpResponse> {
    try {
      const fingerprint = await this.getFingerprint();
      const platform = Capacitor.getPlatform();

      url = this.normalizeUrl(url);

      const options: IRequest = {
        url: url,
        headers: {
          "X-App-Version-id": store.state.currentAppVersion,
          "Content-Type": "application/json",
          "X-App-Type": "driver",
          "X-App-Platform": platform,
          "X-Timezone-Offset": new Date().getTimezoneOffset().toString(),
          Accept: "*/*",
          Connection: "keep-alive",
          "X-Fingerprint": fingerprint,
          "X-Brand-Id": process.env.VUE_APP_BRAND_ID,
        },
      };

      if (data) {
        method === EWebMethod.GET
          ? (options.params = data)
          : (options.data = data);
      }

      try {
        options.headers["User-Agent"] = navigator.userAgent;
      } catch (e) {
        console.error(e);
        options.headers["User-Agent"] = "Unknown";
      }

      options.headers.AppPlatform = platform;

      if (platform !== "web") {
        try {
          const appInfo = await App.getInfo();
          options.headers.AppBuild = appInfo.build;
        } catch (e) {
          console.warn("Failed to get App info", e);
        }
      }

      let token: string | null = null;
      let secretKey: string | null = null;
      try {
        token = await UtilStorage.getItem("token");
        secretKey = await UtilStorage.getItem("secret_key");
      } catch (e) {
        console.warn("Failed to read auth data from storage", e);
      }

      if (token) {
        options.headers["Authorization"] = `Bearer ${token}`;
      }

      const response = await this.makeNetworkRequest(options, method, url);

      const newSecretKey =
        response.headers["x-secret-key"] || response.headers["X-Secret-Key"];
      if (newSecretKey) {
        try {
          await UtilStorage.setItem("secret_key", newSecretKey);
        } catch (e) {
          console.warn("Failed to save secret_key", e);
        }
      }

      if (!response || response.status === 408) {
        Sentry.captureMessage(`408 timeout: ${url}`);
        return {
          url: url,
          status: 0,
          data: { detail: "time_out" },
        };
      }

      if (response.status === 401 && retryOnUnauthorized) {
        if (this.shouldSkipRecovery(url)) {
          return response;
        }

        if (secretKey) {
          try {
            const recoveryResponse = await this.call(
              EWebMethod.POST,
              "auth/user/login/recovery-cookies",
              { token: secretKey },
              false,
            );

            if (recoveryResponse.status === 200) {
              return this.call(method, url, data, false);
            }
          } catch (e) {
            console.error(e);
            Sentry.captureException(e);
          }
        }

        try {
          await UtilStorage.clearAuthData();
        } catch (e) {
          console.warn("Failed to clear auth data", e);
        }
        window.location.href = "/";
      }

      if (response && ![200, 201, 204].includes(response.status)) {
        if (![401, 429].includes(response.status)) {
          Sentry.withScope((scope) => {
            scope.setLevel("warning");
            scope.setTag("Interceptor", "http_error");
            scope.setTag("platform", Capacitor.getPlatform());
            scope.setTag("status", String(response.status));
            scope.setContext("api_request", {
              url,
              method,
              data: data ? JSON.stringify(data).substring(0, 500) : null,
            });
            scope.setContext("api_response", {
              status: response.status,
              data: response.data
                ? JSON.stringify(response.data).substring(0, 1000)
                : null,
              headers: response.headers,
            });
            Sentry.captureMessage(`HTTP Error: ${url} [${response.status}]`);
          });
        }
      }

      return response;
    } catch (error) {
      let detail = "internal_error";
      const message = error instanceof Error ? error.message : String(error);

      if (
        error instanceof TypeError ||
        (error instanceof Error &&
          (/Failed to fetch/i.test(message) ||
            /Network request failed/i.test(message) ||
            /net::ERR_INTERNET_DISCONNECTED/i.test(message) ||
            /net::ERR_NAME_NOT_RESOLVED/i.test(message) ||
            /net::ERR_NETWORK_CHANGED/i.test(message) ||
            /net::ERR_PROXY_CONNECTION_FAILED/i.test(message)))
      ) {
        detail = "network_error";
      } else if (
        message.includes("UtilStorage") ||
        message.includes("getItem") ||
        message.includes("setItem") ||
        message.includes("localStorage") ||
        message.includes("secure storage")
      ) {
        detail = "storage_error";
      } else if (
        message.includes("Capacitor") ||
        message.includes("App.getInfo") ||
        message.includes("getPlatform")
      ) {
        detail = "platform_error";
      } else if (message.includes("getFingerprint")) {
        detail = "fingerprint_error";
      }

      Sentry.withScope((scope) => {
        scope.setLevel("error");
        scope.setTag("Interceptor", detail);
        scope.setTag("platform", Capacitor.getPlatform());
        scope.setContext("api_request", {
          url,
          method,
        });
        scope.setContext("error_details", {
          message,
          stack: error instanceof Error ? error.stack : null,
          original_error: error,
        });
        Sentry.captureMessage(`API Critical Error: ${url} [${detail}]`);
      });

      return {
        status: 0,
        data: {
          detail,
          message,
        },
        url: url,
      };
    }
  }
}

export default $api;
